import React, { ReactElement } from 'react'
import {
  Box,
  Card,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAppSelector } from 'src/store/hooks'
import { selectRoles } from 'src/v3/settings/settings.slice'
import { ComplianceDashboard } from './compliance'
import { PlanningDashboard } from './planning'
import PerformanceDashboard from './performance'
import AccreditationDashboard from './accreditation/accreditation-dashboard'
import InstructorDashboard from './instructor/instructor-dashboard'
import CoursePlanningDashboard from './planning/course/course-planning-dashboard'
import CourseComplianceDashboard from './compliance/course-compliance-dashboard'

type ContentDomain = 'live' | 'enduring'

function determineActiveTab(
  pathname: string,
  hasPlanning: boolean,
  hasCompliance: boolean,
  hasPerformance: boolean,
  hasAccreditation: boolean,
  hasInstructor: boolean,
): string {
  // First check if pathname explicitly includes a tab name
  if (pathname.includes('compliance')) return 'compliance'
  if (pathname.includes('planning')) return 'planning'
  if (pathname.includes('performance')) return 'performance'
  if (pathname.includes('accreditation')) return 'accreditation'
  if (pathname.includes('instructor')) return 'instructor'

  // Fall back to first available role-based tab
  if (hasPlanning) return 'planning'
  if (hasCompliance) return 'compliance'
  if (hasPerformance) return 'performance'
  if (hasAccreditation) return 'accreditation'
  if (hasInstructor) return 'instructor'

  return 'planning'
}

export default (): ReactElement => {
  let location = useLocation()
  let navigate = useNavigate()
  let roles = useAppSelector(selectRoles)

  let hasPlanning = roles.includes('content-planner')
  let hasCompliance = roles.includes('content-compliance')
  let hasPerformance = roles.includes('content-analyzer')
  let hasAccreditation = roles.includes('content-liaison')
  let hasInstructor =
    roles.includes('content-instructor') || roles.includes('content-compliance')

  // Parse URL to determine active tab
  let activeTab = determineActiveTab(
    location.pathname,
    hasPlanning,
    hasCompliance,
    hasPerformance,
    hasAccreditation,
    hasInstructor,
  )

  // Get domain from query params (default to 'live')
  let searchParams = new URLSearchParams(location.search)
  let domain = (searchParams.get('domain') as ContentDomain) || 'live'

  // Handle tab change (preserves domain selection)
  let handleTabChange = (_: React.SyntheticEvent, newTab: string) => {
    navigate(`/pipeline/${newTab}?domain=${domain}`)
  }

  // Handle domain toggle (stays on current tab)
  let handleDomainChange = (
    _: React.MouseEvent<HTMLElement>,
    newDomain: ContentDomain,
  ) => {
    if (newDomain !== null) {
      navigate(`/pipeline/${activeTab}?domain=${newDomain}`)
    }
  }

  // Access control
  if (
    !hasPlanning &&
    !hasCompliance &&
    !hasPerformance &&
    !hasAccreditation &&
    !hasInstructor
  ) {
    return (
      <Box sx={{ pt: 1 }}>
        <Typography variant="h4">Content Pipeline</Typography>
        <Card
          variant="outlined"
          sx={{ mt: 2, p: 3, bgcolor: 'background.paperDark' }}
        >
          <Typography>
            You don't have access to content pipeline features.
          </Typography>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Content Pipeline
      </Typography>
      {/* Top-Level Dashboard Navigation */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Content pipeline dashboard navigation"
      >
        {hasPlanning && (
          <Tab
            label="Planning"
            value="planning"
            id="tab-planning"
            aria-controls="tabpanel-planning"
          />
        )}
        {hasCompliance && (
          <Tab
            label="Compliance Review"
            value="compliance"
            id="tab-compliance"
            aria-controls="tabpanel-compliance"
          />
        )}
        {hasPerformance && (
          <Tab
            label="Performance"
            value="performance"
            id="tab-performance"
            aria-controls="tabpanel-performance"
          />
        )}
        {hasAccreditation && (
          <Tab
            label="Accreditation"
            value="accreditation"
            id="tab-accreditation"
            aria-controls="tabpanel-accreditation"
          />
        )}
        {hasInstructor && (
          <Tab
            label="Instructor"
            value="instructor"
            id="tab-instructor"
            aria-controls="tabpanel-instructor"
          />
        )}
      </Tabs>
      {/* Content Domain Toggle - Hidden for Instructor Dashboard */}
      {activeTab !== 'instructor' && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'medium',
            }}
          >
            Content Type:
          </Typography>
          <ToggleButtonGroup
            value={domain}
            exclusive
            onChange={handleDomainChange}
            size="medium"
            aria-label="Content domain selection"
          >
            <ToggleButton value="live" aria-label="Live events (F5)">
              Live (F5)
            </ToggleButton>
            <ToggleButton value="enduring" aria-label="Enduring courses (F3)">
              Enduring (F3)
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {/* Dashboard Content */}
      {activeTab === 'planning' && hasPlanning && (
        <Box
          role="tabpanel"
          id="tabpanel-planning"
          aria-labelledby="tab-planning"
        >
          {domain === 'live' ? (
            <PlanningDashboard />
          ) : (
            <CoursePlanningDashboard />
          )}
        </Box>
      )}
      {activeTab === 'compliance' && hasCompliance && (
        <Box
          role="tabpanel"
          id="tabpanel-compliance"
          aria-labelledby="tab-compliance"
        >
          {domain === 'live' ? (
            <ComplianceDashboard />
          ) : (
            <CourseComplianceDashboard />
          )}
        </Box>
      )}
      {activeTab === 'performance' && hasPerformance && (
        <Box
          role="tabpanel"
          id="tabpanel-performance"
          aria-labelledby="tab-performance"
        >
          <PerformanceDashboard domain={domain} />
        </Box>
      )}
      {activeTab === 'accreditation' && hasAccreditation && (
        <Box
          role="tabpanel"
          id="tabpanel-accreditation"
          aria-labelledby="tab-accreditation"
        >
          <AccreditationDashboard domain={domain} />
        </Box>
      )}
      {activeTab === 'instructor' && hasInstructor && (
        <Box
          role="tabpanel"
          id="tabpanel-instructor"
          aria-labelledby="tab-instructor"
        >
          <InstructorDashboard />
        </Box>
      )}
    </Box>
  )
}
